import { Badge, Box, Text } from '@nimbus-ds/components';
import { SearchFilterInterfaceIdLabel } from 'commons/types';
import { CreateSavedSearchPopover } from 'domains/Orders/components/SavedSearches';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import ChipWithTooltip from './ChipWithTooltip/ChipWithTooltip';
import { ClearFiltersButton } from './ClearFiltersButton';
import { SaveFiltersButton } from './SaveFiltersButton';
import useResponsive from '../Responsive/useResponsive';

interface SearchFilterAppliedFiltersProps {
  appliedFilters: SearchFilterInterfaceIdLabel[];
  resultCount?: string;
  rightElement?: React.ReactNode;
  desktopWrap?: 'wrap' | 'nowrap';
  isLoading?: boolean;
  showClearFiltersButton?: boolean;
  showSaveFiltersButton?: boolean;
  onDismiss?: (key: string) => any;
  onChange: (value: string) => void;
  onClearFilterButtonClick?: () => void;
  onSaveFilterButtonClick?: () => void;
}

export function SearchFilterAppliedFilters({
  appliedFilters,
  resultCount,
  isLoading = false,
  rightElement,
  desktopWrap = 'nowrap',
  showClearFiltersButton = false,
  showSaveFiltersButton = false,
  onDismiss,
  onChange,
  onClearFilterButtonClick,
  onSaveFilterButtonClick,
}: Readonly<SearchFilterAppliedFiltersProps>) {
  const t = useTranslationOrders('appliedFilters');
  const { isMobile } = useResponsive();
  const handleClick = (id: string) => {
    onDismiss?.(id);
    onChange('');
  };
  const handleClearFilterButtonClick = () => {
    onClearFilterButtonClick?.();
  };
  const handleSaveFilterButtonClick = () => {
    onSaveFilterButtonClick?.();
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      flexWrap={isMobile ? 'wrap' : desktopWrap}
      gap="2"
    >
      <Box
        alignItems="center"
        display="flex"
        gap="2"
        flexWrap={isMobile ? 'wrap' : desktopWrap}
      >
        {!!resultCount && <Text>{resultCount}</Text>}
        {isLoading && <Text.Skeleton width="122px" />}
        {!isLoading &&
          appliedFilters?.map(({ id, label, removable, badgeCount }) => (
            <ChipWithTooltip
              key={id}
              id={id}
              label={label}
              removable={removable ?? true}
              onClick={() => handleClick(id)}
              icon={
                badgeCount !== undefined && (
                  <Badge appearance="neutral" count={badgeCount} />
                )
              }
              tooltipContent={
                id === 'stockIssues' ? t('stockIssuesTooltip') : undefined
              }
            />
          ))}
        {!isLoading && showClearFiltersButton && appliedFilters.length > 1 && (
          <ClearFiltersButton onClick={handleClearFilterButtonClick} />
        )}
        {!isLoading && showSaveFiltersButton && appliedFilters.length > 0 && (
          <CreateSavedSearchPopover onSave={handleSaveFilterButtonClick}>
            <SaveFiltersButton onClick={handleSaveFilterButtonClick} />
          </CreateSavedSearchPopover>
        )}
      </Box>
      <Box>{rightElement}</Box>
    </Box>
  );
}
